import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import { useQuery } from '@tanstack/react-query'
import { GetStaticPaths, GetStaticProps } from 'next'
import { Icon, SEO, ModalAction, Layout } from 'components'

import api from 'lib/api'
import env from 'lib/env'
import { useStore } from 'lib/store'
import { getMetricName } from 'utils/page'
import { formatTextRich } from 'utils/format'
import { getVacancyApplied } from 'services/vacancy'
import { ShareVacancy } from './components/share-vacancy'
import { VacancyContent, CandidatureButton } from './components'
import { AlertCandidatures } from './components/alert-candidatures'
import { AlertProcessing } from './components/alert-processing'
import { buildVacancyToGoogleForJobs, buildVacancyToPage } from 'utils/vacancy'

const VacancyPage = props => {
  const router = useRouter()
  const company = props?.company
  const { user, isLoadingUser } = useStore()
  const [modalSuccess, setModalSuccess] = useState(false)

  const vacancy = buildVacancyToPage(props.vacancy)
  const googleForJobs = buildVacancyToGoogleForJobs(props.vacancy)

  useEffect(() => {
    if (props?.company?.slug !== props?.vacancy?.slug) router.push('/404')
  }, [props])

  const verifyRequiredFields =
    user?.cpf === null ||
    user?.name === null ||
    !!vacancy?.requiredFields?.length ||
    !!vacancy?.language?.length ||
    !!vacancy?.hardSkills?.length

  const { data: isApplied } = useQuery({
    queryKey: ['vacancy-applied', vacancy.id],
    queryFn: () => getVacancyApplied(vacancy.id),
    enabled: !!vacancy.id && !!user
  })

  const seoDescription = formatTextRich(vacancy.description)
    .join('. ')
    .slice(0, 120)

  const handleGoBack = () => {
    if (document.referrer.includes('perfil.vagas')) {
      router.push('/')
      return
    }

    router.back()
  }

  const hasPageBeenViewed = (pageId: number, metricName: string) => {
    const value = sessionStorage.getItem(metricName)
    const viewedPages = value ? JSON.parse(value) : value
    return viewedPages?.some(
      (page: { id: number; metricName: string; userId?: number }) =>
        page.id === pageId &&
        page.metricName === metricName &&
        page.userId === user?.id
    )
  }

  const handleMarkPageAsViewed = async () => {
    try {
      const metricName = getMetricName()
      const viewedPages = JSON.parse(sessionStorage.getItem(metricName) || '[]')
      const vacancyHasViewed = viewedPages.some(
        (page: { id: number; metricName: string; userId?: number }) =>
          page.id === vacancy.id &&
          page.metricName === metricName &&
          !page.userId
      )

      const payload = {
        vacancyId: vacancy.id,
        metricName,
        origin: 'home',
        vacancyHasViewed
      }

      // eslint-disable-next-line dot-notation
      if (user && user?.id !== 0) payload['userId'] = user.id

      await api(
        '/send-vacancies-collect-view',
        { method: 'POST', data: payload },
        'v3'
      )

      viewedPages.push({ id: vacancy.id, metricName, userId: user?.id })
      sessionStorage.setItem(metricName, JSON.stringify(viewedPages))
    } catch (error) {
      console.log('Erro ao coletar dados da vaga visualizada.', error)
    }
  }

  const markPageAsViewed = async () => {
    const metricName = getMetricName()

    if (vacancy?.id && !hasPageBeenViewed(vacancy.id, metricName)) {
      await handleMarkPageAsViewed()
    }
  }

  useEffect(() => {
    const timeoutId = setTimeout(
      () => !isLoadingUser && markPageAsViewed(),
      500
    )
    return () => clearTimeout(timeoutId)
  }, [isLoadingUser, user?.id, vacancy?.id])

  if (props?.company?.slug !== props?.vacancy?.slug) return null

  const isVacancyProcessing = vacancy?.status === 'PROCESSING'

  const isVacancyDisabled =
    props?.vacancy !== undefined &&
    vacancy !== undefined &&
    (!vacancy?.receivingResume || vacancy?.currentState !== 'em_andamento') &&
    !isVacancyProcessing

  const showCandidature =
    !isApplied && !isVacancyDisabled && !isVacancyProcessing

  return (
    <Layout>
      <SEO
        tabName={`Vaga ${vacancy.title}`}
        title={`Veja os detalhes da vaga ${vacancy.title} na empresa ${company.name}`}
        description={seoDescription}
        keywords={`${vacancy.title}, Vaga de Emprego, empregos, vagas na ${company.name}, trabalho na ${company.name}, carreiras, oportunidades, oportunidades de trabalho, recrutamento`}
        image={company.logo}
        googleForJobs={googleForJobs}
        noIndex={isVacancyDisabled}
        slug={props?.company?.slug}
      />

      <section className="w-full flex bg-white relative rm:sticky top-0 left-0 shadow-sm px-4 xl:px-0">
        <div className="max-w-7xl mx-auto w-full flex items-center gap-3 py-3">
          <button type="button" onClick={handleGoBack}>
            <Icon face="arrow_back_ios" size="md" />
          </button>

          <div className="flex flex-col flex-1">
            <h1 className="text-xs sm:text-sm font-semibold leading-5">
              {vacancy.title}
            </h1>
            <strong className="font-medium text-gray-500 text-body2 -mt-1">
              {company.name}
            </strong>
          </div>

          {!isVacancyDisabled && (
            <>
              {isApplied ? (
                <div className="flex items-center justify-center gap-1.5 rounded-lg px-6 bg-success-500/10 h-10 text-success-700">
                  <Icon face="task_alt" />
                  <span className="text-body2">Candidatura já realizada!</span>
                </div>
              ) : (
                <ShareVacancy />
              )}
            </>
          )}
        </div>
      </section>

      {showCandidature && (
        <CandidatureButton
          {...vacancy}
          verifyRequiredFields={verifyRequiredFields}
          onSuccess={() => setModalSuccess(true)}
          disabledListBlocks={
            isVacancyDisabled || verifyRequiredFields || isApplied
          }
        />
      )}

      <AlertCandidatures isOpen={isVacancyDisabled} />
      <AlertProcessing isProcessing={isVacancyProcessing} />

      <ModalAction
        size="480px"
        icon="check_circle"
        iconVariant="round"
        open={modalSuccess}
        title="Candidatura realizada!"
        description={
          <>
            Candidatura com sucesso na vaga <strong>{vacancy?.title}</strong>.
            Você também receberá um e-mail com mais detalhes.
            <br />
            <br />
            <b>ATENÇÃO:</b> A Sólides não cobra por vagas, cursos, exames ou
            materiais de contratação. Desconfie de qualquer solicitação de
            pagamento.
          </>
        }
        cancelButton={{
          action: () => router.push(env.host_profile.concat('/applications')),
          label: 'Minhas candidaturas'
        }}
        confirmButton={{
          action: () => setModalSuccess(false),
          label: 'Ok, entendi!'
        }}
        className="[&>span]:text-success"
      />

      <VacancyContent
        {...vacancy}
        companyName={company?.name}
        isDisabled={isVacancyDisabled}
      />
    </Layout>
  )
}

export const getStaticPaths: GetStaticPaths = () => {
  return { paths: [], fallback: 'blocking' }
}

export const getStaticProps: GetStaticProps = async context => {
  const id = context.params.id

  try {
    const { data: vacancy } = await api(`/home/vacancy/${id}`)
    return {
      props: { vacancy },
      revalidate: 60 * 30 // 30min
    }
  } catch (err) {
    console.error('ISR ERROR', err)
  }

  return { notFound: true }
}

export default VacancyPage
